import lodash from 'lodash'

import {
  FINAL_ORDER_STATES,
  V3_ORDER_FINISHED_STATUSES,
  ORDER_STATUS_UPDATE_STRATEGY,
} from '../constants.js'

const { chunk } = lodash

const isResolvingOrder = ({ exodusStatus }) => !FINAL_ORDER_STATES.has(exodusStatus)

const isResolvingOrdersWithAvailableAssets = ({ order, availableAssets }) => {
  const { fromAsset, toAsset } = order
  return availableAssets[fromAsset] && availableAssets[toAsset] && isResolvingOrder(order)
}

export const shouldCheckOrder = ({ order, availableAssets }) => {
  const { exodusStatus } = order
  const inProgress = exodusStatus === 'in-progress'
  const isSyncing = exodusStatus === 'syncing'

  const isResolvingOrder = isResolvingOrdersWithAvailableAssets({ order, availableAssets })
  return isResolvingOrder && (inProgress || isSyncing)
}

export const batchProcessOrders = async (orders, callback) => {
  const batchSize = 40
  const results = await Promise.all(chunk(orders, batchSize).map(callback))

  return results.flat()
}

export const getOrderUpdates = ({
  v3Order,
  toTx,
  isIndexlessOrder,
  txTypes,
  order,
  orderStatusUpdateStrategy,
  isRestoringAssets,
  toAsset,
}) => {
  const updates = {
    fromTxId: v3Order.fromTransactionId,
    toTxId: v3Order.toTransactionId,
    status: v3Order.status,
  }

  const needsToSetPayoutTx = !!order.txIds?.find?.((tx) => tx.txType === txTypes.DEPOSIT)
  if (needsToSetPayoutTx) {
    updates.txIds = [
      ...order.txIds,
      {
        txType: txTypes.PAYOUT,
        txId: v3Order.toTransactionId,
      },
    ]
  }

  if (
    v3Order.toAmount &&
    !toAsset.currency.defaultUnit(v3Order.toAmount.value).equals(order.toAmount)
  ) {
    // Update order to match amount - sneaky swaps or slippage may cause toAmount to change.
    updates.toAmount = toAsset.currency.defaultUnit(v3Order.toAmount.value)
  }

  const finishedServerSide = V3_ORDER_FINISHED_STATUSES.has(v3Order.status)
  const completedServerSide = finishedServerSide && v3Order.status === 'complete'

  const finishedClientSide = !!toTx
  const completedClientSide = finishedClientSide && !toTx.failed

  if (completedServerSide && isRestoringAssets) {
    updates.status = 'complete-verified'
    return updates
  }

  if (orderStatusUpdateStrategy === ORDER_STATUS_UPDATE_STRATEGY.CLIENT_OR_SERVER) {
    if (finishedServerSide) {
      // Indexless orders will also be complete if they are finished server side.
      updates.status = completedServerSide ? 'complete-verified' : 'failed-final'
      return updates
    }

    if (finishedClientSide) {
      updates.status = completedClientSide ? 'complete-verified' : 'failed-final'
      return updates
    }

    return updates
  }

  if (
    orderStatusUpdateStrategy === ORDER_STATUS_UPDATE_STRATEGY.CLIENT_AND_SERVER &&
    finishedServerSide
  ) {
    if (!completedServerSide) {
      updates.status = 'failed-final'
      return updates
    }

    if (isIndexlessOrder) {
      updates.status = 'complete-verified'
      return updates
    }

    if (finishedClientSide) {
      updates.status = completedClientSide ? 'complete-verified' : 'failed-final'
    }

    return updates
  }

  return updates
}
